import { PRINCIPLE_IDS, principlePresentation, type PrincipleId, type PrincipleStatus } from "@chessla/shared";
import { Chess } from "chess.js";
import type { lookupBookMove } from "@/features/openings";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { titleCase } from "../review-format";
import { BOARD_TACTICAL_ANNOTATIONS } from "./BoardMotifBadge";

export type EvidenceSide = "white" | "black";

export interface GameDetailsEvidenceOccurrence {
  index: number;
  moveNumber: number;
  san: string;
  notation: string;
  side: EvidenceSide;
}

export interface GameDetailsEvidenceRow {
  id: string;
  label: string;
  glyph: string;
  color: string;
  white: GameDetailsEvidenceOccurrence[];
  black: GameDetailsEvidenceOccurrence[];
}

export interface GameDetailsEvidenceSection {
  id: "quality" | "tactical" | "principles";
  label: string;
  rows: GameDetailsEvidenceRow[];
}

type EvidenceDefinition = { key: string; label: string; glyph: string; color: string };

const QUALITY_DEFINITIONS: readonly EvidenceDefinition[] = [
  { key: "checkmate", label: "Checkmate", glyph: "#", color: "#f0ece2" },
  { key: "brilliant", label: "Brilliant", glyph: "!!", color: "#22b8cf" },
  { key: "best", label: "Best", glyph: "★", color: "#81b64c" },
  { key: "excellent", label: "Excellent", glyph: "✓", color: "#5f9f45" },
  { key: "good", label: "Good", glyph: "+", color: "#96a1a8" },
  { key: "book", label: "Book", glyph: "B", color: "#c9a869" },
  { key: "imprecision", label: "Inaccuracy", glyph: "?!", color: "#eab308" },
  { key: "mistake", label: "Mistake", glyph: "?", color: "#f97316" },
  { key: "blunder", label: "Blunder", glyph: "??", color: "#ff3d00" },
];

const TACTICAL_DEFINITIONS: readonly EvidenceDefinition[] = [
  ...Object.entries(BOARD_TACTICAL_ANNOTATIONS).map(([key, annotation]) => ({
    key,
    label: annotation.title,
    glyph: annotation.label,
    color: annotation.color,
  })),
  { key: "time_pressure", label: "Time Pressure", glyph: "TP", color: "#f59e0b" },
  { key: "low_time", label: "Low Time", glyph: "LT", color: "#f59e0b" },
  { key: "opening_phase", label: "Opening Phase", glyph: "OP", color: "#c9a869" },
  { key: "endgame_phase", label: "Endgame Phase", glyph: "EG", color: "#c9a869" },
];

const emptyRow = (section: string, definition: EvidenceDefinition): GameDetailsEvidenceRow => ({
  id: `${section}:${definition.key}`,
  label: definition.label,
  glyph: definition.glyph,
  color: definition.color,
  white: [],
  black: [],
});

const occurrenceFor = (move: AnalyzedMoveResult, index: number): GameDetailsEvidenceOccurrence => {
  const side: EvidenceSide = move.fenBefore.split(" ")[1] === "b" ? "black" : "white";
  return {
    index,
    moveNumber: move.moveNumber,
    san: move.san,
    notation: `${move.moveNumber}${side === "black" ? "..." : "."}${move.san}`,
    side,
  };
};

const append = (row: GameDetailsEvidenceRow, occurrence: GameDetailsEvidenceOccurrence) => {
  row[occurrence.side].push(occurrence);
};

const principleLabel = (principle: PrincipleId, status: PrincipleStatus) =>
  `${status === "applied" ? "Applied" : "Missed"} · ${titleCase(principlePresentation(principle).name.toLowerCase())}`;

export function buildGameDetailsEvidence(
  moves: readonly AnalyzedMoveResult[],
  bookLookup: typeof lookupBookMove | null = null,
): GameDetailsEvidenceSection[] {
  const qualityRows = QUALITY_DEFINITIONS.map((definition) => emptyRow("quality", definition));
  const qualityByKey = new Map(QUALITY_DEFINITIONS.map((definition, index) => [definition.key, qualityRows[index]]));
  const tacticalRows = TACTICAL_DEFINITIONS.map((definition) => emptyRow("tactical", definition));
  const tacticalByKey = new Map(TACTICAL_DEFINITIONS.map((definition, index) => [definition.key, tacticalRows[index]]));
  const principleRows = new Map<string, GameDetailsEvidenceRow>();
  (["applied", "missed"] as const).forEach((status) =>
    PRINCIPLE_IDS.forEach((principle) => {
      const key = `${status}:${principle}`;
      principleRows.set(
        `principle:${key}`,
        emptyRow("principle", {
          key,
          label: principleLabel(principle, status),
          glyph: status === "applied" ? "+" : "−",
          color: status === "applied" ? "#81b64c" : "#ff3d12",
        }),
      );
    }),
  );

  moves.forEach((move, index) => {
    const occurrence = occurrenceFor(move, index);
    const motifs = new Set((move.motifs ?? []).map((motif) => motif.replace(/^opponent_/, "")));
    motifs.delete("book");
    if (bookLookup) {
      try {
        const played = new Chess(move.fenBefore).move(move.san);
        if (played && bookLookup(move.fenBefore, `${played.from}${played.to}${played.promotion ?? ""}`))
          motifs.add("book");
      } catch {
        // Invalid legacy positions cannot establish documented opening membership.
      }
    }
    motifs.forEach((motif) => {
      if (motifs.has("book") && motif !== "book") return;
      const row = qualityByKey.get(motif);
      if (row) append(row, occurrence);
    });
    new Set([...motifs, ...(move.tacticalFindings ?? []).map(({ motif }) => motif)]).forEach((motif) => {
      const row = tacticalByKey.get(motif);
      if (row) append(row, occurrence);
    });
    new Set((move.principleSignals ?? []).map(({ principle, status }) => `${status}:${principle}`)).forEach((key) => {
      const id = `principle:${key}`;
      const row = principleRows.get(id);
      if (row) append(row, occurrence);
    });
  });

  const orderedPrinciples = (["applied", "missed"] as const).flatMap((status) =>
    PRINCIPLE_IDS.map((principle) => principleRows.get(`principle:${status}:${principle}`)).filter(
      (row): row is GameDetailsEvidenceRow => row != null,
    ),
  );
  return [
    { id: "quality", label: "MOVE QUALITY", rows: qualityRows },
    { id: "tactical", label: "TACTICAL / PATTERN EVIDENCE", rows: tacticalRows },
    { id: "principles", label: "PRINCIPLES", rows: orderedPrinciples },
  ];
}
